import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import apiService, { User, LoginRequest } from '../services/apiService';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  updateUser: (userData: Partial<User>) => Promise<boolean>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const logout = useCallback(() => {
    apiService.logout();
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Check for existing token on app load
  useEffect(() => {
    const savedToken = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
        setIsAuthenticated(true);

        // TODO: Verify token validity by fetching user profile when API is ready
        // For now, trust the stored token and user data
      } catch (error) {
        console.error('Failed to parse saved user data:', error);
        logout();
      }
    }
    setLoading(false);
  }, [logout]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      const loginData: LoginRequest = { email, password };
      const response = await apiService.login(loginData);
      
      // Set initial state from login response
      setToken(response.access_token);
      setUser(response.user);
      setIsAuthenticated(true);
      
      // Fetch complete user profile after successful login (optional)
      try {
        const fullProfile = await apiService.getUserProfile();
        setUser(fullProfile);
      } catch (profileError) {
        console.warn('Failed to fetch full user profile, continuing with login data:', profileError);
        // Continue with basic user data from login response - this is not a critical failure
      }
      
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const savedRefreshToken = localStorage.getItem('refresh_token');

      if (!savedRefreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiService.refreshToken(savedRefreshToken);

      // Update state with id_token as the access token
      setToken(response.id_token);

      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  };

  const updateUser = async (userData: Partial<User>): Promise<boolean> => {
    try {
      const updatedUser = await apiService.updateUserProfile(userData);
      setUser(updatedUser);
      return true;
    } catch (error) {
      console.error('User update failed:', error);
      return false;
    }
  };

  const value = {
    isAuthenticated,
    user,
    token,
    login,
    logout,
    refreshToken,
    updateUser,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};